// Dev-only helper to force API calls to fail, so error states can be inspected.
//
// Enable from the browser console:
//   breadgptForceErrors('all')          -> every API call fails
//   breadgptForceErrors('/chats')       -> only URLs containing "/chats" fail
//   breadgptForceErrors('/users,/roles')-> multiple substrings (comma-separated)
//   breadgptForceErrors(null)           -> turn off
// It is off unless the flag is explicitly set, and never affects real users.
//
// The chat stream has its own failure vocabulary (timeout, rate_limit, auth, …)
// that cannot be reproduced by breaking the network, so pick one by code:
//   breadgptForceStreamError('timeout')  -> next chat message fails that way
//   breadgptForceStreamError()           -> list the available codes
//   breadgptForceStreamError(null)       -> turn off
// The flag clears itself after one use, so a retry succeeds. Codes ending in
// ':something' are variants of one code that the backend words differently
// (e.g. the four distinct reasons for a `permission` failure).

const KEY = 'breadgpt_force_errors'

export function shouldForceError(url?: string): boolean {
  try {
    const v = localStorage.getItem(KEY)
    if (!v) return false
    if (v === 'all') return true
    return v
      .split(',')
      .map(s => s.trim())
      .some(s => s && (url || '').includes(s))
  } catch {
    return false
  }
}

// An axios-error-shaped object so error handlers that read `response.data.detail`
// show a realistic message.
export function forcedError() {
  return {
    isForcedError: true,
    message: 'Forced error (dev)',
    response: {
      status: 500,
      data: { detail: 'Forced error — this is what a failure looks like.' },
    },
  }
}

// Expose the console toggle.
;(window as unknown as Record<string, unknown>).breadgptForceErrors = (
  patternOrNull: string | null
) => {
  if (patternOrNull) localStorage.setItem(KEY, patternOrNull)
  else localStorage.removeItem(KEY)
  return `force-errors = ${localStorage.getItem(KEY) ?? '(off)'}`
}

// ── forced stream errors ─────────────────────────────────────────────────────

const STREAM_KEY = 'breadgpt_force_stream_error'

// Keep these in step with StreamErrorCode in the agent's src/models.py.
const STREAM_ERRORS: Record<
  string,
  { error: string; retryable: boolean; detail: string }
> = {
  timeout: {
    error:
      'BreadGPT took too long to answer and the request timed out. A narrower question usually comes back faster.',
    retryable: true,
    detail: 'forced: APITimeoutError',
  },
  rate_limit: {
    error:
      'BreadGPT is handling a lot of requests right now. Please try again in a moment.',
    retryable: true,
    detail: 'forced: RateLimitError',
  },
  connection: {
    error:
      'The connection was interrupted before BreadGPT finished answering. Please try again.',
    retryable: true,
    detail: 'forced: stream ended without a done marker',
  },
  upstream_unavailable: {
    error: 'The AI service is temporarily unavailable. Please try again.',
    retryable: true,
    detail: 'forced: InternalServerError',
  },
  auth: {
    error:
      'BreadGPT could not authenticate with the AI service. Contact an administrator.',
    retryable: false,
    detail: 'forced: AuthenticationError',
  },
  // A 403 has several distinct real causes; forcing just one of them makes the
  // others easy to forget. Use the `:variant` suffixes below to see each.
  permission: {
    error: 'You do not have access to The Bread Factory data.',
    retryable: false,
    detail: 'forced: 403 — resolve_company, requested company not permitted',
  },
  'permission:no-role': {
    error:
      'You do not have access to the BreadGPT assistant. Ask an administrator to grant you a role in Users & Roles.',
    retryable: false,
    detail: 'forced: 403 — resolve_company, no chat permission at all',
  },
  'permission:lost-access': {
    error: "You do not have access to Gail's data.",
    retryable: false,
    detail:
      'forced: 403 — continuing an existing chat whose company you no longer hold',
  },
  'permission:other-users-chat': {
    error: 'You are not allowed to access this chat.',
    retryable: false,
    detail: 'forced: 403 — chat belongs to another user',
  },
  bad_request: {
    error:
      'BreadGPT could not process that request – the conversation may be too long. Try starting a new chat.',
    retryable: false,
    detail: 'forced: BadRequestError',
  },
  storage: {
    error: 'BreadGPT answered but could not save this chat to your history.',
    retryable: false,
    detail: 'forced: CosmosHttpResponseError',
  },
  unknown: {
    error:
      'Something went wrong while generating a response. Please try again.',
    retryable: true,
    detail: 'forced: RuntimeError',
  },
}

export interface ForcedStreamError {
  code: string
  error: string
  retryable: boolean
  detail?: string | null
}

// Returns a forced error for this URL once, then disarms itself so the user can
// exercise the retry path without editing localStorage again.
export function forcedStreamError(url?: string): ForcedStreamError | null {
  try {
    const code = localStorage.getItem(STREAM_KEY)
    if (!code) return null
    const spec = STREAM_ERRORS[code]
    if (!spec) return null
    localStorage.removeItem(STREAM_KEY)
    void url
    // Variants share their base code: the UI keys off StreamErrorCode, and
    // `permission:no-role` is still a `permission` failure.
    return { code: code.split(':')[0], ...spec }
  } catch {
    return null
  }
}

;(window as unknown as Record<string, unknown>).breadgptForceStreamError = (
  code?: string | null
) => {
  if (code === undefined) {
    return `codes: ${Object.keys(STREAM_ERRORS).join(', ')}`
  }
  if (code) {
    if (!STREAM_ERRORS[code]) {
      return `unknown code "${code}" — try: ${Object.keys(STREAM_ERRORS).join(', ')}`
    }
    localStorage.setItem(STREAM_KEY, code)
  } else {
    localStorage.removeItem(STREAM_KEY)
  }
  return `force-stream-error = ${localStorage.getItem(STREAM_KEY) ?? '(off)'}`
}
